import { UserCheck, Users, Handshake, Send, ArrowRight } from "lucide-react";
import { toast } from "sonner";

const cards = [
    {
        icon: Users,
        title: "One point of contact",
        body: "Reduce the number of representatives involved in coordinating your cases and product needs.",
        testId: "surgeon-card-one-contact",
    },
    {
        icon: UserCheck,
        title: "Support from someone you trust",
        body: "Work with a rep who already understands your preferences, workflow, facility, and surgical team.",
        testId: "surgeon-card-trust",
    },
    {
        icon: Handshake,
        title: "Back the reps who show up",
        body: "Create more opportunities for the dependable representatives who have invested their time in supporting you and your patients.",
        testId: "surgeon-card-back-reps",
    },
];

export default function SurgeonSection() {
    return (
        <section
            id="surgeons"
            data-testid="surgeon-section"
            className="bg-white border-b border-slate-200 scroll-mt-24"
        >
            <div className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-28">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 mb-16">
                    <div className="lg:col-span-6">
                        <p className="eyebrow">For Surgeons</p>
                        <h2
                            data-testid="surgeon-headline"
                            className="font-display mt-4 text-3xl md:text-5xl font-semibold tracking-tight text-slate-900 leading-[1.1]"
                        >
                            One trusted relationship.
                            <br />
                            <span className="text-slate-500">
                                More streamlined support.
                            </span>
                        </h2>
                    </div>
                    <div className="lg:col-span-6 lg:pt-3 space-y-4">
                        <p className="text-base md:text-lg text-slate-600 leading-relaxed">
                            The reps who earn a surgeon&rsquo;s trust are the ones who
                            consistently show up — early mornings, late nights,
                            weekends, and every case in between. They understand your
                            preferences, your team, and how you operate.
                        </p>
                        <p className="text-base md:text-lg text-slate-600 leading-relaxed">
                            1099er helps qualified reps bring complementary,
                            non-conflicting technologies into the relationships they
                            already support. That can mean fewer industry contacts to
                            coordinate, greater continuity in the OR, and more
                            opportunities for your most dependable reps to support your
                            practice.
                        </p>
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    {cards.map((c) => {
                        const Icon = c.icon;
                        return (
                            <div
                                key={c.title}
                                data-testid={c.testId}
                                className="group bg-[#FAFAFA] border border-slate-200 rounded-2xl p-7 hover:-translate-y-1 hover:shadow-lg hover:shadow-slate-200/60 transition-all duration-300"
                            >
                                <div className="w-10 h-10 rounded-lg bg-slate-900 flex items-center justify-center">
                                    <Icon size={18} className="text-white" />
                                </div>
                                <h3 className="font-display mt-6 text-xl font-semibold text-slate-900">
                                    {c.title}
                                </h3>
                                <p className="mt-3 text-sm md:text-base text-slate-600 leading-relaxed">
                                    {c.body}
                                </p>
                            </div>
                        );
                    })}
                </div>

                <div
                    data-testid="surgeon-share-cta"
                    className="mt-12 rounded-2xl bg-slate-900 text-white p-7 md:p-9"
                >
                    <div className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-6 md:gap-8 md:items-center">
                        <div className="max-w-2xl">
                            <p className="text-[11px] font-medium uppercase tracking-[0.14em] text-slate-400 flex items-center gap-2">
                                <Send size={12} /> For Surgeons
                            </p>
                            <h3 className="font-display mt-3 text-2xl md:text-3xl font-semibold tracking-tight leading-tight">
                                Have a rep in mind?
                            </h3>
                            <p className="mt-3 text-sm md:text-base text-slate-300 leading-relaxed">
                                Send your rep the 1099er onboarding link so they can
                                add complementary, non-conflicting lines and keep
                                supporting your practice.
                            </p>
                        </div>
                        <ShareWithRepButtons />
                    </div>
                </div>

                <p
                    data-testid="surgeon-footer-note"
                    className="mt-10 text-sm text-slate-500 italic leading-relaxed max-w-3xl"
                >
                    Clinical needs, product quality, hospital policies, and patient
                    outcomes always come first.
                </p>
            </div>
        </section>
    );
}

function ShareWithRepButtons() {
    const url = "https://1099er.co/#join";
    const subject = "1099er — worth a look";
    const body =
        "I came across 1099er — a private network that helps experienced reps " +
        "add complementary, non-conflicting product lines to what they already " +
        "carry. Thought it might be worth a look:\n\n" +
        url +
        "\n\nThanks";
    const mailto = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;

    const nativeShare = async (e) => {
        if (navigator.share) {
            e.preventDefault();
            try {
                await navigator.share({
                    title: "1099er — for medical sales reps",
                    text: "Check out 1099er — worth a look.",
                    url,
                });
            } catch (err) {
                // AbortError is expected when the user dismisses the native
                // share sheet; anything else we log for debuggability.
                if (err?.name !== "AbortError") {
                    console.warn("Native share failed:", err);
                }
            }
        }
        // otherwise fall through to mailto anchor default behavior
    };

    const copyLink = async () => {
        try {
            await navigator.clipboard.writeText(url);
            toast.success("Signup link copied to clipboard.");
        } catch (err) {
            console.warn("Clipboard copy failed:", err);
            toast.error("Could not copy link.");
        }
    };

    return (
        <div className="flex flex-col sm:flex-row gap-3 self-start md:self-auto">
            <a
                href={mailto}
                onClick={nativeShare}
                data-testid="surgeon-share-primary-btn"
                className="inline-flex items-center justify-center gap-2 bg-white text-slate-900 text-sm font-medium px-6 py-3 rounded-md hover:bg-slate-100 transition-colors"
            >
                Send to your rep
                <ArrowRight size={14} />
            </a>
            <button
                type="button"
                onClick={copyLink}
                data-testid="surgeon-share-copy-btn"
                className="inline-flex items-center justify-center gap-2 bg-transparent text-white border border-white/25 text-sm font-medium px-6 py-3 rounded-md hover:bg-white/5 transition-colors"
            >
                Copy signup link
            </button>
        </div>
    );
}
